
import type { PropType } from 'vue'
import type { FeatureIdEnum } from '../../../enums/feature-id.enum'
import type { DataTableColumn } from '../../../models'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import { EpbText, KpiBar } from '../..'
import {
  useFilters,
  useToolingReportCtrPerformance,
  useToolingReportList,
  useToolingReportStats,
} from '../../../composables'
import DataTable from '../../crud/data-table.component.vue'
import PaginationBar from '../../crud/pagination-bar.component.vue'
import ToolReportingDataStateHandler, { DataStateEnum } from './data-state-handler.vue'

export default defineComponent({
  name: 'ToolReportingTable',
  components: {
    DataTable,
    EpbText,
    PaginationBar,
    KpiBar,
    ToolReportingDataStateHandler,
  },
  props: {
    tool: {
      type: String as PropType<FeatureIdEnum>,
      required: true,
    },
  },
  setup(props) {
    const {
      proxy: { $epb },
    } = getCurrentInstance()
    const baseClass = 'epb-tool-reporting-table'
    const messages = $epb.messages.insights.toolReporting
    const tableData = ref([])

    const {
      fetchPaginatedData,
      itemsPerPageOptions,
      loading,
      pagination,
      resetPage,
      setToolingPagination,
      setToolingSorting,
      sort,
    } = useToolingReportList(props.tool)
    const { stats } = useToolingReportStats(props.tool)
    const { getBarColor } = useToolingReportCtrPerformance()

    const { selectedDates } = useFilters()

    const isDateSelected = computed(() => selectedDates.value[0])

    const columns: Array<DataTableColumn> = [
      { id: 'name', text: messages.columns.name, sortable: true },
      { id: 'queries', text: messages.columns.queries },
      { id: 'ctr', text: messages.columns.ctr, sortable: true },
    ]

    const dataState = computed((): DataStateEnum => {
      if (loading.value) {
        return DataStateEnum.LOADING
      }
      if (tableData.value?.length) {
        return DataStateEnum.LOADED
      }

      return DataStateEnum.NO_RESULTS
    })

    function formatNumberAsPercentage(number: number): string {
      return `${$epb.utils.formatNumber(number)}%`
    }

    async function fetchTableData(): Promise<void> {
      if (isDateSelected.value) {
        tableData.value = await fetchPaginatedData()
      }
    }

    async function onPaginationChange(): Promise<void> {
      await fetchTableData()
    }

    async function onSortChange(): Promise<void> {
      resetPage()
      await fetchTableData()
    }

    fetchTableData()

    // TODO: Move to the composable?
    watch(selectedDates, fetchTableData)
    watch(pagination, onPaginationChange)
    watch(sort, onSortChange)

    return {
      baseClass,
      columns,
      formatNumberAsPercentage,
      getBarColor,
      dataState,
      itemsPerPageOptions,
      loading,
      messages,
      pagination,
      setToolingPagination,
      setToolingSorting,
      sort,
      tableData,
      stats,
    }
  },
})
